import { KalturaMetaDataType } from '@kaltura-ott/ott-client-types/api/types';

import {
  AssetImageSourceEnum,
  AssetPresentationFormEnum,
  AssetPresentationRatioEnum,
  ButtonTypes,
  DisplayType,
  LCBatchesPresentationOrderEnum,
  MenuItemPin,
  RatingProviderEnum,
  RecordingTypeEnum,
  SearchKeys,
  ShowMoreActions,
  TypeOfApplicationLink,
  TypesContinueWatching,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
} from '../../../interfaces';
import {
  CATEGORY_ID_SYSTEM_NAME,
  KUX_FEATURE_NAME,
  META_ENUMERATION_TYPE,
  META_ENUMERATION_TYPE_MULTIPLE,
  META_NULLABLE,
  METADATA_FEATURE_NAME,
  READ_ONLY_FEATURE_NAME,
} from '../../utils/constants';

export interface MetaDynamicData {
  [key: string]: string | number | boolean | undefined;
  defaultValue?: string | number;
  required?: boolean | string;
}

/**
 *
 * @category Basic KUX Configuration
 */
export interface InitialMetaConst {
  name: string;
  dataType: KalturaMetaDataType;
  features: string[];
  helpText: string;
  multipleValue?: boolean;
  dynamicData?: MetaDynamicData;
  tags?: (
    | keyof typeof UIDAPresentationTypeEnum
    | keyof typeof UIDAStructureTypeEnum
    | keyof typeof RatingProviderEnum
    | keyof typeof LCBatchesPresentationOrderEnum
    | keyof typeof AssetPresentationFormEnum
    | keyof typeof TypeOfApplicationLink
    | keyof typeof AssetImageSourceEnum
    | keyof typeof AssetPresentationRatioEnum
    | keyof typeof RecordingTypeEnum
    | keyof typeof TypesContinueWatching
    | keyof typeof ShowMoreActions
    | keyof typeof MenuItemPin
    | keyof typeof DisplayType
    | keyof typeof ButtonTypes
    | keyof typeof SearchKeys
  )[];
  isProtected?: boolean;
}

/**
 * @category Basic KUX Configuration
 */
export const CATEGORY_ID: { [key: string]: InitialMetaConst } = {
  [CATEGORY_ID_SYSTEM_NAME]: {
    name: "Category ID (don't use it)",
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME, READ_ONLY_FEATURE_NAME, METADATA_FEATURE_NAME],
    helpText: 'For internal use only!!!',
    isProtected: true,
  },
};

/**
 * @category Basic KUX Configuration
 */
export const BASE_KUX_METAS: { [key: string]: InitialMetaConst } = {
  UIDA_structure_type: {
    multipleValue: true,
    name: 'UIDA Structure Type',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE],
    helpText: 'The type of UI. Num.',
    /**
     * [[UIDAStructureTypeEnum]]
     */
    tags: ['aside', 'root', 'page', 'widget', 'menu', 'subpage'],
  },
  UIDA_presentation_type: {
    multipleValue: true,
    name: 'UIDA Presentation Type',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE],
    helpText: 'The actual UI definition of certain presentations.',
    /**
     * [[UIDAPresentationTypeEnum]]
     */
    tags: [
      'container',
      'menu',
      'hero',
      'cw',
      'collection',
      'footer',
      'apps',
      'zapper',
      'folder',
      'login',
      'settings',
      'subscription',
      'blocked_channels',
      'parental_control',
      'asset_details_page',
      'epg',
      'applications',
      'language',
      'favorites',
      'devices',
      'link_list',
      'transaction_history',
      'npvr',
      'preferred_video_quality',
      'series_catch_up',
      'grid',
      'menu_item',
      'menu_sub',
      'tvod',
      'suggested', // Rail
      'channel_api', // Rail
      'search',
      'series_recordings', // Page
      'player',
      'device_info',
      'deeplink',
    ],
  },
};

/**
 * @category Basic KUX Configuration
 */
export const STYLING_KUX_METAS: { [key: string]: InitialMetaConst } = {
  background_color: {
    name: 'Background Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'The background color of the page. Hex Color Code',
  },
  text_color: {
    name: 'Text Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'The background color of the page. Hex Color Code',
  },
  font_type: {
    name: 'Font Type',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Design component. Font Type (Font Family). String.',
  },
  font_size: {
    name: 'Font Size',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Font Size Numerical (relative number)',
  },
  focus_color: {
    name: 'Focus Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Text focused Color. Hex Color Code',
  },
  selected_color: {
    name: 'Selected Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Text Selected Color. Hex Color Code',
  },

  btn_font_size: {
    name: 'Btn Font Size',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Text size in button (relative number)',
  },
  btn_text_color: {
    name: 'Btn Default Text Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Default Text Color. Hex Color Code',
  },
  btn_text_color_focus: {
    name: 'Btn Focused Text Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Focused Text Color. Hex Color Code',
  },

  btn_background_color: {
    name: 'Btn Default Background Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'The background color of the btn. Hex Color Code',
  },
  btn_background_color_focus: {
    name: 'Btn Focused Background Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'The background color of the focused btn. Hex Color Code',
  },
  btn_background_opacity: {
    name: 'Btn Background Opacity',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'The opacity of background color on the btn. Numerical (0-100)',
  },
  color_past: {
    name: 'Past Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Past Events Color (Zapper/EPG). Hex Color Code',
  },
  color_present: {
    name: 'Present Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Present Events Color (Zapper/EPG). Hex Color Code',
  },
  color_future: {
    name: 'Future Color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Future Events Color (Zapper/EPG). Hex Color Code',
  },
};

/**
 * @category Basic KUX Configuration
 */
export const CONTENT_KUX_METAS: { [key: string]: InitialMetaConst } = {
  parental_age_groups: {
    name: 'Parental Age Group',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'List of parental age groups.',
  },
  asset_struct: {
    name: 'Asset Type',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Asset Type for Asset Details Page (Struct)',
  },
  global_linear_channels_collection_id: {
    name: 'The collection id of the collection that groups linear channels.',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: `The collection id of the collection that groups linear channels.
    To be cached at the session initiation process and use on the application at Hero, Zapper, and EPG page.`,
  },
  cache_linear_channel_list: {
    name: 'On Load Caching',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `To Cache or Not Linear Channels`,
  },
  present_favorite_indicator: {
    name: 'Favorite Indicator',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `Each item on the widget will present a favorite
    indicator, in case it was favorite by the user.`,
    dynamicData: {
      defaultValue: 'false',
    },
  },
  display_logout_button: {
    name: 'Display Logout Button',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `Logout button will be displayed according to value`,
    dynamicData: {
      defaultValue: 'false',
    },
  },
  present_entitlement_indicator: {
    name: 'Entitlement Indicator',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `Each item on the widget will present an entitlement indicator,
    (usually a currency sign), in case the user is not entitled to the asset.`,
    dynamicData: {
      defaultValue: 'false',
    },
  },
  linear_channels_filtering_kSQL: {
    name: 'Linear Channels Filtering K-SQL',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: `The filtering kSQL is added to any asset request on the Linears channel request,
    with an “AND“ operator. The other filtering requests are also concatenated.`,
  },
  filtering_kSQL: {
    name: 'Filtering K-SQL',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: `The filtering kSQL is added to any asset request on the widget with an 'AND' operator.\n
    The containing page’s or other filtering requests are also concatenated.`,
  },
  collection_filtering_kSQL: {
    name: 'Collection Filtering K-SQL',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: `Additional KSQL string for search filtering`,
  },
  enable_filter_by_entitlement: {
    name: 'Filter by Entitlement',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `When this switch is turned ON, unentitled assets will be removed from the rail`,
  },

  favorites_ttl: {
    name: 'Favorite TTL',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: `Time expiration to address the BE for fresh Favorites Data in order to align with other HH devices.
Will be measured in minutes.`,
  },

  hide_adult_assets: {
    name: 'Hide Adult Assets',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `The assets with the 'adult' flag with 'True' value,
    will be hidden according to the presence of the TRUE value.
    The assets will be hidden on all the widgets of the page.`,
    dynamicData: {
      defaultValue: 'true',
    },
  },
  hide_adult_assets_kSQL: {
    name: 'Hide Adult Filtering K-SQL',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: `The kSQL will be added and used on any page/widget
    which will turn on the 'hide_adult_assets' flag.`,
  },

  /**
   * [See Presentation component filtering](https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/1716388105/Pages+Widgets#Presentation-component-filtering)
   */
  hide_parental_assets: {
    name: 'Hide Parental Assets',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `The assets with the 'parental rating' higher than the user’s
    'age_threshold', will be hidden according to the presence of the TRUE value.
    The assets will be hidden on all the widgets of the page`,
    dynamicData: {
      defaultValue: 'false',
    },
  },
  parental_pin_protected: {
    name: 'Protect With Parental PIN',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `Will require parental PIN`,
  },
  hide_parental_assets_kSQL: {
    name: 'Hide Parental Filtering K-SQL',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: `The kSQL will be added and used on any page/widget
     which will turn on the 'hide_parental_assets' flag.`,
  },

  /**
   * [See Presentation component filtering](https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/1716388105/Pages+Widgets#Presentation-component-filtering)
   */
  user_type_denial_list_for_ui_components: {
    name: 'User Type Denial List For UI Components',
    dataType: KalturaMetaDataType.string,
    multipleValue: true,
    features: [KUX_FEATURE_NAME],
    helpText: `List of user types to be denied access to the page.`,
  },

  /**
   * [See Presentation component filtering](https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/1716388105/Pages+Widgets#Presentation-component-filtering)
   */
  device_brand_denial_list_for_ui_components: {
    name: 'Brand Denial List For UI Components',
    dataType: KalturaMetaDataType.string,
    multipleValue: true,
    features: [KUX_FEATURE_NAME],
    helpText: `List of brand ids to be denied access to the page.`,
  },

  seek_with_thumbnails_denial_list: {
    name: 'Brand Denial List For Thumbnails',
    dataType: KalturaMetaDataType.string,
    multipleValue: true,
    features: [KUX_FEATURE_NAME],
    helpText: `List of brand ids to be denied access to thumbnails.`,
  },
  keep_alive_beacons_frequency: {
    name: 'Keep alive beacons frequency',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: `Keep alive events to Broadpeak at a configurable frequency.`,
  },
  hide_prices: {
    name: 'Hide Price',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Flag to indicate hiding prices for purchased add-ones',
  },
  allow_cancellation: {
    name: 'Allow Cancellation',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Flag to indicate presenting the user with cancel button for add-on type subscriptions ',
  },
  addon_collection: {
    name: 'Addons Collection Id',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Collection id holding all available add-ons for purchase ',
  },
  not_applicable_collection: {
    name: 'Not Applicable Collection Id',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Collection id holding all not applicable add-ons for purchase ',
  },
  hero_collection: {
    name: 'Hero Collection Id',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Collection id holding the hero id',
  },

  external_portal_url: {
    name: 'External URL (portal/self care)',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'URL reference to the external portal/self-care to make purchases ',
  },
  prime_time: {
    name: 'Prime Time',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Prime Time Hour',
  },
  max_epg_date_backward: {
    name: 'Number of Backwards EPG Days',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Must be lower or equal then rolling buffer configuration',
    dynamicData: {
      required: true,
      defaultValue: 7,
    },
  },
  max_epg_date_forward: {
    name: 'Number of Forward EPG Days',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Must be lower then ingested programs allowed date',
    dynamicData: {
      required: true,
      defaultValue: 7,
    },
  },
  new_episode_badge_days: {
    name: 'New Episode Badge',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText:
      'Should be displayed only for Series recording cards. Indication of new recordings will be determined by calculation of the Program Air Date [startDate field].',
    dynamicData: {
      required: true,
      defaultValue: 7,
    },
  },
};
/**
 * @category Basic KUX Configuration
 */
export const WIDGET_KUX_METAS: { [key: string]: InitialMetaConst } = {
  present_app_icon_platform_denylist: {
    name: 'App Icon by Brand Deny List',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    multipleValue: true,
    helpText: 'List of the brand ids on which the app icon will not be presented on the menu.',
  },
  present_clock_platform_denylist: {
    name: 'Clock Icon by Brand Deny List',
    dataType: KalturaMetaDataType.string, //
    features: [KUX_FEATURE_NAME],
    multipleValue: true,
    helpText: 'List of the brand ids on which the CLOCK icon will NOT be shown.',
  },

  /**
   * TODO: <Final list of values TBD> AssetPresentationForm
   */
  asset_presentation_form: {
    name: 'Asset Image Presentation Form',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE, META_NULLABLE],
    multipleValue: true,
    helpText: 'Design component. The presentation of the assets.',
    /**
     * [[AssetPresentationFormEnum]]
     */
    tags: ['circle'],
  },
  asset_image_source: {
    name: 'Asset Image Source',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE, META_NULLABLE],
    multipleValue: true,
    helpText: 'Design component. The source of image for each asset',
    /**
     * [[AssetPresentationFormEnum]]
     */
    tags: ['manual'],
  },
  asset_presentation_ratio: {
    name: 'Asset Image Ratio - DEPRECATED',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE],
    multipleValue: true,
    helpText: 'Design component. The presentation of the assets. DEPRECATED - use Asset Image Type',
    /**
     * [[AssetPresentationRatioEnum]]
     */
    tags: ['1x1', '16x9', '3x4', '2x3', '5x3'],
    dynamicData: {
      defaultValue: '16x9',
    },
  },
  asset_presentation_image_type: {
    name: 'Asset Image Type',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME, 'metadata'],
    helpText: 'Design component. The presentation image type of the assets.',
  },
  channel_presentation_image_type: {
    name: 'Image Type For Logos',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Image type for linear channels logo on hero rail',
  },
  group_by_is_enabled: {
    name: 'Group by is enabled',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'default value true',
  },
  group_by_object: {
    name: 'Group by object',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'example: {"groupBy": {"objectType": "KalturaAssetMetaOrTagGroupBy","value": "SeriesID"}}',
  },
  group_by_unmatched_items_policy: {
    name: 'Group by unmatched items policy',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'example: {"unmatchedItemsPolicy": "INCLUDE_AND_MERGE"}',
  },
  group_by_representative_policy_object: {
    name: 'Group by representative policy object',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText:
      'example: {"selectionPolicy": {"objectType": "KalturaTopSubscriptionEntitledRsp", "orderBy": {"objectType": "KalturaAssetOrder", "orderBy": "RELEVANCY_DESC"}}',
  },
  order_by_policy_object: {
    name: 'Order_by_policy_object',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText:
      'example: {"orderBy": {"objectType": "KalturaAssetDynamicOrder", "orderBy": "META_ASC", "name": "EpisodeNumber"}}',
  },
  asset_image_height: {
    name: 'Asset Image Height',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Design component. The representation of image height, basically Rail height',
    dynamicData: {
      defaultValue: 200,
      required: true,
    },
  },
  default_channel: {
    name: 'Default Asset on the Load',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'The asset id of the default asset to stream on the load.',
  },
  present_player: {
    name: 'Present Player',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Controls the presentation of the player above the linear channel rail.',
  },
  present_X_most_watched_count: {
    name: 'X Most Watched Assets',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'The number of X most-watched assets on the device.',
  },
  present_X_most_watched_period: {
    name: 'X Weeks for Most Watched Assets',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: `The number of weeks to go back to count the watch attempts.
    Max 12 weeks`,
  },

  /**
   * TODO: Need to be defined what type of custom field should be used.
   * NOTE: order of tags/values is important.
   * [See Channel Presentation Flow](https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/1716388105/Pages+Widgets?focusedCommentId=1752467894#Channel-Presentation-Flow)
   */
  linear_channels_batches_presentation_order: {
    name: 'LC Presentation Order',
    dataType: KalturaMetaDataType.string,
    multipleValue: true,
    features: [KUX_FEATURE_NAME],
    helpText: `The array with 1-3 values will appear.
    If the enum representing the batch appears, it means that the batch will be presented.`,
    /**
     * [[LCBatchesPresentationOrderEnum]]
     */
    tags: ['fav', 'top_x', 'lcn'],
  },
  present_current_programs: {
    name: 'Present Current Program',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `Defines whether the current program for each linear channel will be presented or not.`,
  },
  present_filter_entitled: {
    name: 'Present Filter Entitled',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `default value: 1`,
  },
  present_filter_favorite: {
    name: 'Present Filter Favorite',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `default value: 1`,
  },
  present_filter_X_watched: {
    name: 'Present Filter X Watched',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `default value: 1`,
  },
  program_refresh_frequency: {
    name: 'Program Refresh Frequency',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Frequency of program data refreshing. In SECOND default = 300 (5 min)',
  },
  present_mute_platform_denylist: {
    name: 'Present Mute_platform_denylist',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    multipleValue: true,
    helpText: `List of the brand ids on which the mute will not be presented on the menu.`,
  },
  present_rating: {
    name: 'Present Rating',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Presenting the rating on the rail item.',
    dynamicData: {
      defaultValue: 'false',
    },
  },
  present_show_more: {
    name: 'Show more in END of the rail',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText:
      `If the value is True → present the “more“ button on the end of the rail.` +
      `If the value is False → do not present the “more“ button on the end of the rail.` +
      `The link from the “more“ button`,
    dynamicData: {
      defaultValue: 'false',
    },
  },
  rail_items_limit: {
    name: 'Limit Items on Rail ',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'The number of items to present on the rail.',
    dynamicData: {
      defaultValue: 20,
    },
  },
  /**
   * TODO: Need to be defined the list of providers
   * [See Rating provider source DMS definition](https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/1716388105/Pages+Widgets?focusedCommentId=1752467894#Configurations-shared-by-multiple-components)
   */
  rating_provider: {
    name: 'Rating Provider',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE, META_NULLABLE],
    helpText: 'Presenting the rating on the rail item.',
    /**
     * [[RatingProviderEnum]]
     */
    tags: ['imdb', 'kp', 'star', 'o2', 'tvprofi'],
  },

  present_rating_source_logo: {
    name: 'Present Rating Provider Logo',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Presenting the source of the rating. ',
    dynamicData: {
      defaultValue: 'false',
    },
  },

  /**
   * [See Application Links Types](https://kaltura.atlassian.net/l/c/sBgTsMpw)
   */
  type_of_app_link: {
    name: 'In Application Link Type',
    dataType: KalturaMetaDataType.string,
    multipleValue: true,
    features: ['metadata', KUX_FEATURE_NAME, META_ENUMERATION_TYPE],
    helpText: 'Presenting if link is external, internal or something else',
    /**
     * [[TypeOfApplicationLink]]
     */
    tags: ['page', 'asset', 'external', 'grid'],
  },

  /**
   * [See Application Links Types](https://kaltura.atlassian.net/l/c/sBgTsMpw)
   */
  in_app_link: {
    name: 'In Application Link',
    dataType: KalturaMetaDataType.string,
    features: ['metadata', KUX_FEATURE_NAME],
    helpText: 'Link to specific Page or Asset or external',
  },
  button_type: {
    name: 'Button Type',
    dataType: KalturaMetaDataType.string,
    features: ['metadata', KUX_FEATURE_NAME, META_ENUMERATION_TYPE],
    helpText: 'If asset used as BUTTON',
    tags: ['login', 'sign_up'],
  },
  recording_type: {
    name: 'Recordings Type',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE],
    multipleValue: true,
    helpText: 'What type of recordings should be displayed',
    /**
     * [[AssetPresentationFormEnum]]
     */
    tags: ['all', 'latest', 'series'],
  },
  typeIn: {
    name: 'Types of Medias',
    dataType: KalturaMetaDataType.string,
    features: ['metadata', KUX_FEATURE_NAME],
    helpText:
      'If omitted all types should be included. All types include VOD, Episodes, Linear.\n' +
      'The value is taken from the list of types defined in the DMS for Movies, Episodes, Series.',
  },
  statusEqual: {
    name: 'Type of Recently Watched',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE],
    multipleValue: true,
    helpText:
      'Which type of recently watched media to include in the result – those that finished watching, \n' +
      'those that are in progress or both. If omitted or specified filter = all – return all types. \n' +
      'Allowed values: progress – return medias that are in-progress, done – return medias that finished watching.',
    /**
     * [[TypesContinueWatching]]
     */
    tags: ['ALL', 'PROGRESS', 'DONE'],
  },
  signup_url: {
    name: 'Sign Up Url',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'URL for the sign up page in external systems ',
  },
  target_grid_category_id: {
    name: 'Target Grid Page',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText:
      'To open the exact type of the “Grid” page the “Tree Category Item Id“ of the “Grid“ page should be sent as a parameter',
  },
  hide_titles: {
    name: 'Hide Titles',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Hides titles for rail',
  },
  use_suppress: {
    name: 'Use Suppress',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText:
      'If this field set to True,\n' +
      'Specific modification need to be used on the API,\n' +
      'Then only last watched episode on the series will be available on the service.',
  },
  // TODO <Should be based on Metas <of type tag> defined on basic structs>
  recommendation_fields: {
    name: 'Recommendations Fields',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    multipleValue: true,
    helpText: 'The list of the meta fields on which the recommendations are built ',
  },
  search_channel_phrase: {
    name: 'Search Channel Phrase',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText:
      `The Content Administrator will put the keyword in the USDA's ` +
      `“search_channel_phrase“ and the first channel whose “channelDisplayName” ` +
      `contains this phrase will be connected to the rail.`,
  },
  search_channel_package_phrase: {
    name: 'Primary Search for the package',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Primary Search for the package',
  },
  purchase_allow_coupons: {
    name: 'Allow Coupons',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'This flag will indicate if the purchase page of this asset shall preset the coupon input option',
  },
  rental_badge_boundary: {
    name: 'Rental Badge Expiration',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText:
      'Will be used as a boundary for displaying the expiration badge.\n' +
      'Badge will be displayed only if expiration time passed the value of this field.\n' +
      'Will be measured in hours. ',
  },
  enable_filter_episodes_by_entitlement: {
    name: 'Enable filter episodes by entitlement',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText:
      'Determine if Series Episodes will be Filtered By Entitlement.\n' +
      'This filter will be applied only for Series Details Page',
  },
  recording_expiration_boundary: {
    name: 'Recording Badge Expiration',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText:
      'Will be used as a boundary for displaying the Expiration Badges on the card,\n' +
      'The expiration badge will be displayed only if expiration time was equal \\ lower then this field value,\n' +
      'Will be measured in days.',
  },
  npvr_unlimited_quota: {
    name: 'Unlimited NPvR Quota',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText:
      'Will be boolean value to indicate if unlimited storage will be used,\n' +
      'If set to True, Quota won’t be displayed.',
  },
  group_by: {
    name: 'Group By Meta',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText:
      'In case the value will be empty, not grouping will occur. \n' +
      'In case the value will contain any asset’s meta field value, the assets in the rail to be aggregated by the mentioned value. ',
  },
  series_type_scheduled_recordings_rail_title: {
    name: 'Recording Rail Title (PoEditor Key)',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText:
      'The key for translation in PoEditor for title of the Series Future Recordings [Type Series] Rail within the page.',
  },
  menu_item_pined: {
    multipleValue: true,
    name: 'Pin Menu Item',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE, META_NULLABLE],
    helpText: 'Pin menu item to left or right side',
    /**
     * [[UIDAStructureTypeEnum]]
     */
    tags: ['left', 'right'],
  },
  purchase_device_brand_denial_list: {
    name: 'Brand Denial List for Purchase',
    dataType: KalturaMetaDataType.string,
    multipleValue: true,
    features: [KUX_FEATURE_NAME],
    helpText: `List of brand ids where purchase button will be replaced with “purchase not allowed“ message`,
  },
  anchors: {
    name: 'Page Anchors',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_NULLABLE],
    multipleValue: true,
    helpText: 'Tag with multiple values. Unique values across the Category Tree',
  },
  display_type: {
    multipleValue: true,
    name: 'Display on Menu',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE],
    helpText: 'Menu link presentation mode',
    tags: ['text', 'image', 'both'],
  },
  concurrency_buymore: {
    name: 'Buy More Stream',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `Boolean parameter to indicate if “Buy More Stream” shall be presented `,
  },
  show_top_shelf_rail_ui: {
    name: 'Attach Top Shelf Widget to the Rail',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: `Boolean parameter to know if show rail with Top Shelf widget `,
  },
  concurrency_player_error_ttl: {
    name: 'Player Error TTL',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: `This value will hold the expiry, in second for the error message shown on the Maxi player in case of cunncurency limit reached`,
  },
  attached_collection_id: {
    name: 'Collection Id',
    dataType: KalturaMetaDataType.number,
    features: ['metadata', KUX_FEATURE_NAME],
    helpText: 'Collection to be shown on Landing Page',
  },
  default_linear_channel: {
    name: 'Default Channel',
    dataType: KalturaMetaDataType.number,
    features: ['metadata', KUX_FEATURE_NAME],
    helpText: 'Default Linear Channel ID to be shown.',
  },
  end_search_time_in_milliseconds: {
    name: 'End of search (milliseconds)',
    dataType: KalturaMetaDataType.number,
    features: ['metadata', KUX_FEATURE_NAME],
    helpText: 'End typing interval',
    dynamicData: {
      required: true,
    },
  },
  recommendations_collection_id: {
    name: 'Recommendation Collection Id',
    dataType: KalturaMetaDataType.number,
    features: ['metadata', KUX_FEATURE_NAME],
    helpText: 'Recommended assets collection',
    dynamicData: {
      required: true,
    },
  },
  search_main_discovery_attributes: {
    name: 'Search Keys',
    multipleValue: true,
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME, META_ENUMERATION_TYPE_MULTIPLE],
    helpText: 'Search keys',
    dynamicData: {
      required: true,
    },
    tags: ['name', 'original_name', 'actors', 'director'],
  },
  sorting_type: {
    name: 'Sort By Meta',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Results Sorting Field',
    dynamicData: {
      required: true,
    },
  },
  max_numbers_of_history_search_list: {
    name: 'Max items in history list',
    dataType: KalturaMetaDataType.number,
    features: ['metadata', KUX_FEATURE_NAME],
    helpText: 'Max number of items to save in history list',
    dynamicData: {
      required: true,
      defaultValue: 10,
    },
  },
  main_progress_bar_color: {
    name: 'Main progress bar color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Progress Bar indication for the current playable content.',
  },
  secondary_progress_bar_color: {
    name: 'Secondary progress bar color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Progress Bar indication for the Live point.',
  },
  placeholder_progress_bar_color: {
    name: 'Placeholder progress bar color',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'The original color of the progress Bar.',
  },
  on_hover_color: {
    name: 'hover color for main progress bar',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'The color of main progress bar color will be changed to this value at On Hover state',
  },
  display_btn_epg_navigation: {
    name: 'Display Epg Navigation',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Determine if EPG Button should be displayed on Player View',
  },
  jump_to_live_after_pause_x_minutes: {
    name: 'Jump to live after pause (minutes)',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'time for jumping to live after pause',
    dynamicData: {
      defaultValue: 60,
    },
  },
  player_one_click_move_in_sec: {
    name: 'Player on click move (seconds)',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Player on click move',
    dynamicData: {
      defaultValue: -30,
    },
  },
  second_button_action: {
    name: 'Second button action',
    dataType: KalturaMetaDataType.string,
    /**
     * SecondButtonActions
     */
    features: [KUX_FEATURE_NAME],
    helpText: 'second button action in maxi player action',
  },
  check_connectivity_when_buffer_after_sec: {
    name: 'Connectivity timeout buffer (seconds)',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Check internet connectivity if buffering time exceeds this duration. (seconds)',
    dynamicData: {
      defaultValue: 20,
    },
  },
  livedvr_default_time: {
    name: 'Live DVR default time (minutes)',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Live DVR default time',
    dynamicData: {
      defaultValue: 60,
    },
  },
  live_popup_time_in_sec: {
    name: 'Live popup time (seconds)',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Live popup time',
    dynamicData: {
      defaultValue: 5,
    },
  },
  multicast_endpoint: {
    name: 'Multicast endpoint',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Provider end_point URL to understand if the channel is within IPTV network.',
    dynamicData: {
      defaultValue: '',
    },
  },

  multicast_indication_value: {
    name: 'Multicast indication value',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Network Mode Indication',
    dynamicData: {
      defaultValue: 'networkMode',
    },
  },
  multicast_pause_resume_ABR_threshold: {
    name: 'Multicast pause resume ABR threshold',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText:
      'The K-UX application shall ensure that a subsequent seek forwards does not continue past multicast edge minus',
    dynamicData: {
      defaultValue: 30,
    },
  },

  WVCAS_capable: {
    name: 'WVCAS capable',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Is WV DRM Supported',
    dynamicData: {
      defaultValue: 'false',
    },
  },
  HEVC_capable: {
    name: 'HEVC capable',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Is HEVC codec Supported',
    dynamicData: {
      defaultValue: 'false',
    },
  },

  DRM_capable: {
    name: 'DRM capable',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Is DRM Supported',
    dynamicData: {
      defaultValue: 'true',
    },
  },

  HEVC_DENY_LIST: {
    name: 'HEVC DENY LIST',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'A list of Make and Models to be denied for HEVC (need to be codec as AVC). Separated with a comma',
    dynamicData: {
      defaultValue: '',
    },
  },
  DRM_DENY_LIST: {
    name: 'DRM DENY LIST',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'A list of Make and Models to be denied for DRM (need to be sent as Clear). Separated with a comma',
    dynamicData: {
      defaultValue: '',
    },
  },

  HEVC_ERRORS_FALLBACK_LIMITAION: {
    name: 'HEVC ERRORS FALLBACK LIMITAION',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Accumulation number of HEVC playback errors from which the device HEVC capability is changed to false',
    dynamicData: {
      defaultValue: 20,
    },
  },

  zapperDisplayEnabled: {
    name: 'zapperDisplayEnabled',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Enable or Disable Zapper display on player view',
    dynamicData: {
      defaultValue: 'true',
    },
  },

  default_audio_codes: {
    name: 'Default audio codes',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'The account default by order audio codes selection separated by comma',
    dynamicData: {
      defaultValue: '',
    },
  },

  default_subtitles_codes: {
    name: 'Default Subtitle codes',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'The account default by order Subtitles Language Codes separated by comma',
    dynamicData: {
      defaultValue: '',
    },
  },

  player_language_codes: {
    name: 'Player Language Codes',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'The account available player code preferences for audio and subtitles.',
  },

  zapper_show_duration_in_seconds: {
    name: 'Zapper show duration',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'How long time show the zapper. In SECOND default = 5 (5 sec)',
    dynamicData: {
      required: true,
      defaultValue: 5,
    },
  },

  image_height_compact: {
    name: 'Compact width',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'for screen width < 600dp',
  },

  image_height_medium: {
    name: 'Medium width',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'for screen width: 600dp ≤ width < 840dp',
  },

  display_name: {
    name: 'Display name',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'App tab title name',
  },
  enable_idle_message: {
    name: 'Enable idle message',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Display and apply Idle Message Rules',
  },
  enable_user_idle_message_management: {
    name: 'Enable user idle message management',
    dataType: KalturaMetaDataType.boolean,
    features: [KUX_FEATURE_NAME],
    helpText: 'Will indicate if a User can manage Idle Message feature',
  },
  idle_message_lifecycle: {
    name: 'idle message lifecycle',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'The time value that need to be exceeded in order to display the message.',
  },
  idle_message_duration: {
    name: 'idle message duration',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'Duration of the popup display.',
  },
  android_fallback_page_address: {
    name: 'Android Fallback URL',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Address of Application Store Page',
    dynamicData: {
      defaultValue: 'https://play.google.com/store/apps/details?id=',
      required: true,
    },
  },
  apple_fallback_page_address: {
    name: 'Apple Fallback URL',
    dataType: KalturaMetaDataType.string,
    features: [KUX_FEATURE_NAME],
    helpText: 'Address of Application Store Page',
    dynamicData: {
      defaultValue: 'https://itunes.apple.com/app/',
      required: true,
    },
  },
  auto_transition_delay: {
    name: 'Carousel transition time',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME],
    helpText: 'The time in seconds to scroll automatically to next asset',
    dynamicData: {
      defaultValue: 5,
      required: true,
    },
  },
  cover_image_type: {
    name: 'Cover image type',
    dataType: KalturaMetaDataType.number,
    features: [KUX_FEATURE_NAME, 'metadata'],
    helpText: 'Cover image type',
  },
};
